import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../stores/authStore';
import { api } from '../services/api';
import { toast } from 'react-hot-toast';
import { Car } from 'lucide-react';

interface Veiculo {
  id: number;
  placa: string;
  prefixo?: string | null;
  renavam?: string | null;
  chassi?: string | null;
  marca?: string | null;
  modelo?: string | null;
  ano?: number | null;
  cor?: string | null;
  categoria?: string | null;
  combustivel?: string | null;
  seguro?: string | null;
  vigencia_inicio?: string | null;
  vigencia_fim?: string | null;
  apolice?: string | null;
  contrato?: string | null;
  situacao: string;
  observacoes?: string | null;
  criado_em: string;
  atualizado_em?: string | null;
}

const Veiculos: React.FC = () => {
  const { user } = useAuthStore();
  const [veiculos, setVeiculos] = useState<Veiculo[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingVeiculoId, setEditingVeiculoId] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    placa: '',
    prefixo: '',
    renavam: '',
    chassi: '',
    marca: '',
    modelo: '',
    ano: '',
    cor: '',
    categoria: '',
    combustivel: '',
    seguro: '',
    vigencia_inicio: '',
    vigencia_fim: '',
    apolice: '',
    contrato: '',
    situacao: 'Ativo',
    observacoes: ''
  });
  const [editFormData, setEditFormData] = useState({
    placa: '',
    prefixo: '',
    renavam: '',
    chassi: '',
    marca: '',
    modelo: '',
    ano: '',
    cor: '',
    categoria: '',
    combustivel: '',
    seguro: '',
    vigencia_inicio: '',
    vigencia_fim: '',
    apolice: '',
    contrato: '',
    situacao: 'Ativo',
    observacoes: ''
  });

  useEffect(() => {
    if (!user || user.funcao !== 'admin') {
      toast.error('Acesso negado. Apenas administradores podem gerenciar veículos.');
      return;
    }
    
    fetchVeiculos();
  }, [user]);

  const fetchVeiculos = async () => {
    try {
      setLoading(true);
      const response = await api.get('/veiculos');
      const veiculosData = response.data?.veiculos || [];
      setVeiculos(veiculosData);
    } catch (error: any) {
      console.error('❌ Erro ao carregar veículos:', error);
      if (error.response?.status === 401) {
        toast.error('Não autorizado. Faça login novamente.');
      } else if (error.response?.status === 403) {
        toast.error('Acesso negado. Apenas administradores podem acessar esta página.');
      } else {
        toast.error(`Erro ao carregar veículos: ${error.response?.data?.error || error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVeiculo = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const dadosEnvio: any = {
        placa: formData.placa,
        prefixo: formData.prefixo || null,
        renavam: formData.renavam || null,
        chassi: formData.chassi || null,
        marca: formData.marca || null,
        modelo: formData.modelo || null,
        ano: formData.ano ? parseInt(formData.ano) : null,
        cor: formData.cor || null,
        categoria: formData.categoria || null,
        combustivel: formData.combustivel || null,
        seguro: formData.seguro || null,
        vigencia_inicio: formData.vigencia_inicio || null,
        vigencia_fim: formData.vigencia_fim || null,
        apolice: formData.apolice || null,
        contrato: formData.contrato || null,
        situacao: formData.situacao,
        observacoes: formData.observacoes || null
      };

      const response = await api.post('/veiculos', dadosEnvio);
      toast.success('Veículo criado com sucesso!');
      setShowCreateForm(false);
      resetFormData();
      fetchVeiculos();
    } catch (error: any) {
      console.error('❌ Erro ao criar veículo:', error);
      if (error.response?.status === 400) {
        toast.error(`Erro de validação: ${error.response.data.error || 'Dados inválidos'}`);
      } else {
        toast.error(`Erro ao criar veículo: ${error.response?.data?.error || error.message}`);
      }
    }
  };

  const handleEditVeiculo = async (veiculoId: number) => {
    try {
      setLoading(true);
      const response = await api.get(`/veiculos/${veiculoId}`);
      const veiculo = response.data.veiculo;
      
      setEditFormData({
        placa: veiculo.placa || '',
        prefixo: veiculo.prefixo || '',
        renavam: veiculo.renavam || '',
        chassi: veiculo.chassi || '',
        marca: veiculo.marca || '',
        modelo: veiculo.modelo || '',
        ano: veiculo.ano ? veiculo.ano.toString() : '',
        cor: veiculo.cor || '',
        categoria: veiculo.categoria || '',
        combustivel: veiculo.combustivel || '',
        seguro: veiculo.seguro || '',
        vigencia_inicio: veiculo.vigencia_inicio ? new Date(veiculo.vigencia_inicio).toISOString().split('T')[0] : '',
        vigencia_fim: veiculo.vigencia_fim ? new Date(veiculo.vigencia_fim).toISOString().split('T')[0] : '',
        apolice: veiculo.apolice || '',
        contrato: veiculo.contrato || '',
        situacao: veiculo.situacao || 'Ativo',
        observacoes: veiculo.observacoes || ''
      });
      setEditingVeiculoId(veiculoId);
      setShowEditForm(true);
      setShowCreateForm(false);
    } catch (error: any) {
      console.error('❌ Erro ao buscar veículo:', error);
      if (error.response?.status === 404) {
        toast.error('Veículo não encontrado');
      } else {
        toast.error(`Erro ao carregar dados do veículo: ${error.response?.data?.error || error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateVeiculo = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingVeiculoId) return;

    try {
      const updateData: any = {
        placa: editFormData.placa,
        prefixo: editFormData.prefixo || null,
        renavam: editFormData.renavam || null,
        chassi: editFormData.chassi || null,
        marca: editFormData.marca || null,
        modelo: editFormData.modelo || null,
        ano: editFormData.ano ? parseInt(editFormData.ano) : null,
        cor: editFormData.cor || null,
        categoria: editFormData.categoria || null,
        combustivel: editFormData.combustivel || null,
        seguro: editFormData.seguro || null,
        vigencia_inicio: editFormData.vigencia_inicio || null,
        vigencia_fim: editFormData.vigencia_fim || null,
        apolice: editFormData.apolice || null,
        contrato: editFormData.contrato || null,
        situacao: editFormData.situacao,
        observacoes: editFormData.observacoes || null
      };

      const response = await api.put(`/veiculos/${editingVeiculoId}`, updateData);
      toast.success('Veículo atualizado com sucesso!');
      setShowEditForm(false);
      setEditingVeiculoId(null);
      resetEditFormData();
      fetchVeiculos();
    } catch (error: any) {
      console.error('❌ Erro ao atualizar veículo:', error);
      if (error.response?.status === 400) {
        toast.error(`Erro de validação: ${error.response.data.error || 'Dados inválidos'}`);
      } else {
        toast.error(`Erro ao atualizar veículo: ${error.response?.data?.error || error.message}`);
      }
    }
  };

  const handleDeleteVeiculo = async (veiculoId: number) => {
    if (!window.confirm('Tem certeza que deseja excluir este veículo?')) {
      return;
    }

    try {
      await api.delete(`/veiculos/${veiculoId}`);
      toast.success('Veículo excluído com sucesso!');
      fetchVeiculos();
    } catch (error: any) {
      console.error('❌ Erro ao excluir veículo:', error);
      toast.error(`Erro ao excluir veículo: ${error.response?.data?.error || error.message}`);
    }
  };

  const resetFormData = () => {
    setFormData({
      placa: '',
      prefixo: '',
      renavam: '',
      chassi: '',
      marca: '',
      modelo: '',
      ano: '',
      cor: '',
      categoria: '',
      combustivel: '',
      seguro: '',
      vigencia_inicio: '',
      vigencia_fim: '',
      apolice: '',
      contrato: '',
      situacao: 'Ativo',
      observacoes: ''
    });
  };

  const resetEditFormData = () => {
    setEditFormData({
      placa: '',
      prefixo: '',
      renavam: '',
      chassi: '',
      marca: '',
      modelo: '',
      ano: '',
      cor: '',
      categoria: '',
      combustivel: '',
      seguro: '',
      vigencia_inicio: '',
      vigencia_fim: '',
      apolice: '',
      contrato: '',
      situacao: 'Ativo',
      observacoes: ''
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Veículos</h1>
        <button
          onClick={() => {
            setShowCreateForm(true);
            setShowEditForm(false);
            setEditingVeiculoId(null);
            resetFormData();
          }}
          className="w-full sm:w-auto bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
        >
          <Car className="h-5 w-5" />
          Novo Veículo
        </button>
      </div>

      {/* Formulário de edição */}
      {showEditForm && (
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md mb-6">
          <h2 className="text-lg font-semibold mb-4">Editar Veículo</h2>
          <form onSubmit={handleUpdateVeiculo} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Placa *</label>
                <input
                  type="text"
                  value={editFormData.placa}
                  onChange={(e) => setEditFormData({...editFormData, placa: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Prefixo</label>
                <input
                  type="text"
                  value={editFormData.prefixo}
                  onChange={(e) => setEditFormData({...editFormData, prefixo: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">RENAVAM</label>
                <input
                  type="text"
                  value={editFormData.renavam}
                  onChange={(e) => setEditFormData({...editFormData, renavam: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Chassi</label>
                <input
                  type="text"
                  value={editFormData.chassi}
                  onChange={(e) => setEditFormData({...editFormData, chassi: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Marca</label>
                <input
                  type="text"
                  value={editFormData.marca}
                  onChange={(e) => setEditFormData({...editFormData, marca: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Modelo</label>
                <input
                  type="text"
                  value={editFormData.modelo}
                  onChange={(e) => setEditFormData({...editFormData, modelo: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Ano</label>
                <input
                  type="number"
                  value={editFormData.ano}
                  onChange={(e) => setEditFormData({...editFormData, ano: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="1900"
                  max="2100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Cor</label>
                <input
                  type="text"
                  value={editFormData.cor}
                  onChange={(e) => setEditFormData({...editFormData, cor: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Categoria</label>
                <input
                  type="text"
                  value={editFormData.categoria}
                  onChange={(e) => setEditFormData({...editFormData, categoria: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Combustível</label>
                <select
                  value={editFormData.combustivel}
                  onChange={(e) => setEditFormData({...editFormData, combustivel: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Selecione</option>
                  <option value="Gasolina">Gasolina</option>
                  <option value="Etanol">Etanol</option>
                  <option value="Flex">Flex</option>
                  <option value="Diesel">Diesel</option>
                  <option value="GNV">GNV</option>
                  <option value="Elétrico">Elétrico</option>
                  <option value="Híbrido">Híbrido</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Seguro</label>
                <input
                  type="text"
                  value={editFormData.seguro}
                  onChange={(e) => setEditFormData({...editFormData, seguro: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Vigência Início</label>
                <input
                  type="date"
                  value={editFormData.vigencia_inicio}
                  onChange={(e) => setEditFormData({...editFormData, vigencia_inicio: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Vigência Fim</label>
                <input
                  type="date"
                  value={editFormData.vigencia_fim}
                  onChange={(e) => setEditFormData({...editFormData, vigencia_fim: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Apólice</label>
                <input
                  type="text"
                  value={editFormData.apolice}
                  onChange={(e) => setEditFormData({...editFormData, apolice: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contrato</label>
                <select
                  value={editFormData.contrato}
                  onChange={(e) => setEditFormData({...editFormData, contrato: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Selecione</option>
                  <option value="Consolare">Consolare</option>
                  <option value="FVB">FVB</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Situação</label>
                <select
                  value={editFormData.situacao}
                  onChange={(e) => setEditFormData({...editFormData, situacao: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Ativo">Ativo</option>
                  <option value="Inativo">Inativo</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Observações</label>
              <textarea
                value={editFormData.observacoes}
                onChange={(e) => setEditFormData({...editFormData, observacoes: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="submit"
                className="w-full sm:w-auto bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Salvar Alterações
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowEditForm(false);
                  setEditingVeiculoId(null);
                  resetEditFormData();
                }}
                className="w-full sm:w-auto bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Formulário de criação */}
      {showCreateForm && (
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md mb-6">
          <h2 className="text-lg font-semibold mb-4">Cadastrar Veículo</h2>
          <form onSubmit={handleCreateVeiculo} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Placa *</label>
                <input
                  type="text"
                  value={formData.placa}
                  onChange={(e) => setFormData({...formData, placa: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Prefixo</label>
                <input
                  type="text"
                  value={formData.prefixo}
                  onChange={(e) => setFormData({...formData, prefixo: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">RENAVAM</label>
                <input
                  type="text"
                  value={formData.renavam}
                  onChange={(e) => setFormData({...formData, renavam: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Chassi</label>
                <input
                  type="text"
                  value={formData.chassi}
                  onChange={(e) => setFormData({...formData, chassi: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Marca</label>
                <input
                  type="text"
                  value={formData.marca}
                  onChange={(e) => setFormData({...formData, marca: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Modelo</label>
                <input
                  type="text"
                  value={formData.modelo}
                  onChange={(e) => setFormData({...formData, modelo: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Ano</label>
                <input
                  type="number"
                  value={formData.ano}
                  onChange={(e) => setFormData({...formData, ano: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="1900"
                  max="2100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Cor</label>
                <input
                  type="text"
                  value={formData.cor}
                  onChange={(e) => setFormData({...formData, cor: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Categoria</label>
                <input
                  type="text"
                  value={formData.categoria}
                  onChange={(e) => setFormData({...formData, categoria: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Combustível</label>
                <select
                  value={formData.combustivel}
                  onChange={(e) => setFormData({...formData, combustivel: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Selecione</option>
                  <option value="Gasolina">Gasolina</option>
                  <option value="Etanol">Etanol</option>
                  <option value="Flex">Flex</option>
                  <option value="Diesel">Diesel</option>
                  <option value="GNV">GNV</option>
                  <option value="Elétrico">Elétrico</option>
                  <option value="Híbrido">Híbrido</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Seguro</label>
                <input
                  type="text"
                  value={formData.seguro}
                  onChange={(e) => setFormData({...formData, seguro: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Vigência Início</label>
                <input
                  type="date"
                  value={formData.vigencia_inicio}
                  onChange={(e) => setFormData({...formData, vigencia_inicio: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Vigência Fim</label>
                <input
                  type="date"
                  value={formData.vigencia_fim}
                  onChange={(e) => setFormData({...formData, vigencia_fim: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Apólice</label>
                <input
                  type="text"
                  value={formData.apolice}
                  onChange={(e) => setFormData({...formData, apolice: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contrato</label>
                <select
                  value={formData.contrato}
                  onChange={(e) => setFormData({...formData, contrato: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Selecione</option>
                  <option value="Consolare">Consolare</option>
                  <option value="FVB">FVB</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Situação</label>
                <select
                  value={formData.situacao}
                  onChange={(e) => setFormData({...formData, situacao: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Ativo">Ativo</option>
                  <option value="Inativo">Inativo</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Observações</label>
              <textarea
                value={formData.observacoes}
                onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="submit"
                className="w-full sm:w-auto bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
              >
                Cadastrar
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  resetFormData();
                }}
                className="w-full sm:w-auto bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Lista de veículos */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Placa</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prefixo</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Marca/Modelo</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ano</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Combustível</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contrato</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Situação</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {veiculos.map((veiculo) => (
                <tr key={veiculo.id} className="hover:bg-gray-50">
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{veiculo.placa}</td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">{veiculo.prefixo || '-'}</td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {veiculo.marca && veiculo.modelo ? `${veiculo.marca} ${veiculo.modelo}` : '-'}
                  </td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">{veiculo.ano || '-'}</td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">{veiculo.combustivel || '-'}</td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">{veiculo.contrato || '-'}</td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      veiculo.situacao === 'Ativo' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {veiculo.situacao}
                    </span>
                  </td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex flex-col sm:flex-row gap-2">
                      <button
                        onClick={() => handleEditVeiculo(veiculo.id)}
                        className="text-blue-600 hover:text-blue-900 text-xs sm:text-sm"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => handleDeleteVeiculo(veiculo.id)}
                        className="text-red-600 hover:text-red-900 text-xs sm:text-sm"
                      >
                        Excluir
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {veiculos.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            Nenhum veículo cadastrado
          </div>
        )}
      </div>
    </div>
  );
};

export default Veiculos;


